import type { useGT } from 'gt-react';
import { PricelistVersionSource } from '@erp/shared-types';

type Translate = ReturnType<typeof useGT>;

// What produced each version of a pricelist's tables.
const SOURCE_LABELS: Record<PricelistVersionSource, (gt: Translate) => string> =
  {
    [PricelistVersionSource.Upload]: (gt) =>
      gt('Initial extraction', {
        context: 'pricelist version history: version created by the upload',
      }),
    [PricelistVersionSource.Extraction]: (gt) =>
      gt('Re-extracted', {
        context:
          'pricelist version history: version created by re-running the extraction',
      }),
    [PricelistVersionSource.Edit]: (gt) =>
      gt('Table edits saved', {
        context:
          'pricelist version history: version created by saving table edits',
      }),
  };

/** How this version of the pricelist's tables came about. */
export function pricelistVersionSourceLabel(
  gt: Translate,
  source: PricelistVersionSource,
): string {
  return SOURCE_LABELS[source](gt);
}

/** The size of a version's contents, e.g. "2 tables · 128 rows". */
export function pricelistVersionSizeLabel(
  gt: Translate,
  { tableCount, rowCount }: { tableCount: number; rowCount: number },
): string {
  const tables =
    tableCount === 1
      ? gt('1 table', {
          context: 'pricelist version history: single extracted table',
        })
      : gt('{count} tables', {
          context: 'pricelist version history: extracted table count',
          count: String(tableCount),
        });
  const rows =
    rowCount === 1
      ? gt('1 row', { context: 'pricelist version history: single table row' })
      : gt('{count} rows', {
          context: 'pricelist version history: table row count',
          count: String(rowCount),
        });
  return `${tables} · ${rows}`;
}
